import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Form,
  Button,
  Alert,
  Card,
  Row,
  Col,
  Image,
  Spinner,
} from 'react-bootstrap';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { blogsAPI } from '../services/api';
import { API_URL } from '../config';

interface BlogDocument {
  id: number;
  file_name: string;
  file_path: string;
  file_type: 'image' | 'document';
  file_size: number;
}

interface BlogData {
  id?: number;
  title: string;
  content: string;
  excerpt: string;
  category: string;
  status: 'draft' | 'published';
  featured_image?: string;
  documents?: BlogDocument[];
}

// const API_URL = '/api';

const BlogForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const quillRef = useRef<ReactQuill>(null);

  const [formData, setFormData] = useState<BlogData>({
    title: '',
    content: '',
    excerpt: '',
    category: '',
    status: 'draft'
  });

  const [files, setFiles] = useState<FileList | null>(null);
  const [existingDocuments, setExistingDocuments] = useState<BlogDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const insertTable = () => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (range) {
      const tableHTML = `
        <table style="border-collapse: collapse; width: 100%;">
          <tbody>
            <tr>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 1</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 2</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 3</td>
            </tr>
            <tr>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 4</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 5</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 6</td>
            </tr>
            <tr>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 7</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 8</td>
              <td style="border: 1px solid #ddd; padding: 8px;">Cell 9</td>
            </tr>
          </tbody>
        </table>
        <p><br></p>
      `;
      
      quill.clipboard.dangerouslyPasteHTML(range.index, tableHTML);
      quill.setSelection(range.index + 1);
    }
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'align': [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      handlers: {}
    },
    clipboard: {
      matchVisual: false
    }
  }), []);

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'script',
    'list', 'indent',
    'direction', 'align',
    'blockquote', 'code-block',
    'link', 'image', 'video'
  ];

  useEffect(() => {
    if (isEdit && id) {
      loadBlog();
    }
  }, [id, isEdit]);

  const loadBlog = async () => {
    try {
      setLoading(true);
      const response = await blogsAPI.getByIdOrSlug(id!);
      const blog = response.data;

      setFormData({
        title: blog.title || '',
        content: blog.content || '',
        excerpt: blog.excerpt || '',
        category: blog.category || '',
        status: blog.status || 'draft'
      });

      setExistingDocuments(blog.documents || []);
      setError('');
    } catch (err) {
      setError('Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({ ...prev, content: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(e.target.files);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await blogsAPI.deleteDocument(String(documentId));
      setExistingDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setSuccess('File deleted successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete file');
    }
  };



  const getImageUrl = (filePath: string) => {
    if (!filePath) return '';

    // If it's already a full URL, return as is
    if (filePath.startsWith('http')) {
      return filePath;
    }

    // Clean the path
    const cleanPath = filePath.startsWith('/')
      ? filePath.slice(1)
      : filePath;

    // In production, use relative path with proxy
    // In development, use full API URL
    if (process.env.NODE_ENV === 'production') {
      return `/api/${cleanPath}`;
    } else {
      return `${API_URL}/${cleanPath}`;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content);
      submitData.append('excerpt', formData.excerpt);
      submitData.append('category', formData.category);
      submitData.append('status', formData.status);

      if (files) {
        Array.from(files).forEach(file => {
          submitData.append('files', file);
        });
      }

      if (isEdit && id) {
        await blogsAPI.update(String(id), submitData);
        setSuccess('Blog updated successfully');
      } else {
        await blogsAPI.create(submitData);
        setSuccess('Blog created successfully');
      }

      setTimeout(() => {
        navigate('/blogs');
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType === 'image') return '🖼️';
    return '📄';
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading && isEdit) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <div className="text-center" style={{ padding: '60px 0' }}>
            <Spinner 
              animation="border" 
              style={{ color: '#00579a', width: '40px', height: '40px' }}
            />
            <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading blog...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{ 
            color: '#00579a', 
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            {isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h1>
        </div>

        {error && (
          <Alert 
            variant="danger" 
            dismissible 
            onClose={() => setError('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '1.5rem'
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            variant="success" 
            dismissible 
            onClose={() => setSuccess('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #b7eb8f',
              backgroundColor: '#f6ffed',
              marginBottom: '1.5rem'
            }}
          >
            {success}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col lg={8}>
              <Card style={{ 
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
                marginBottom: '1.5rem'
              }}>
                <Card.Body style={{ padding: '24px' }}>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ 
                      fontWeight: 500, 
                      color: '#262626',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Title *
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      placeholder="Enter blog title"
                      required
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ 
                      fontWeight: 500, 
                      color: '#262626',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Excerpt
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Brief description (optional)"
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                    <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      A short summary of the blog post
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Label style={{ 
                        fontWeight: 500, 
                        color: '#262626',
                        fontSize: '14px',
                        marginBottom: 0
                      }}>
                        Content *
                      </Form.Label>
                      <Button 
                        size="sm"
                        type="button"
                        onClick={insertTable}
                        style={{
                          backgroundColor: 'white',
                          color: '#00579a',
                          border: '1px solid #00579a',
                          borderRadius: '4px',
                          padding: '4px 12px',
                          fontSize: '13px',
                          fontWeight: 500,
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.backgroundColor = '#e6f4ff';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        📊 Insert Table
                      </Button>
                    </div>
                    <div className="quill-wrapper">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={formData.content}
                        onChange={handleContentChange}
                        modules={modules}
                        formats={formats}
                        placeholder="Write your blog content here..."
                      />
                    </div>
                    <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      Use the toolbar to format text. Click "Insert Table" button to add a table.
                    </Form.Text>
                  </Form.Group>
                </Card.Body>
              </Card>

              {isEdit && existingDocuments.length > 0 && (
                <Card style={{ 
                  border: 'none',
                  borderRadius: '8px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
                  marginBottom: '1.5rem'
                }}>
                  <Card.Body style={{ padding: '24px' }}>
                    <h5 style={{ 
                      color: '#262626',
                      fontWeight: 600,
                      fontSize: '16px',
                      marginBottom: '20px'
                    }}>
                      Attached Files
                    </h5>
                    <Row>
                      {existingDocuments.map(doc => (
                        <Col md={6} lg={4} key={doc.id} className="mb-3">
                          <Card style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: '6px'
                          }}>
                            <Card.Body style={{ padding: '16px' }}>
                              {doc.file_type === 'image' ? (
                                <Image
                                  src={getImageUrl(doc.file_path)}
                                  alt={doc.file_name}
                                  fluid
                                  className="mb-2"
                                  style={{ 
                                    maxHeight: '150px', 
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                  }}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    console.error('Failed to load image:', doc.file_path);
                                  }}
                                />
                              ) : (
                                <div className="text-center py-3">
                                  <span style={{ fontSize: '3rem' }}>
                                    {getFileIcon(doc.file_type)}
                                  </span>
                                </div>
                              )}
                              <div 
                                className="text-truncate" 
                                title={doc.file_name}
                                style={{ fontSize: '13px', color: '#262626', fontWeight: 500 }}
                              >
                                {doc.file_name}
                              </div>
                              <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '12px' }}>
                                {formatFileSize(doc.file_size)}
                              </div>
                              <Button
                                size="sm"
                                className="w-100"
                                onClick={() => handleDeleteDocument(doc.id)}
                                style={{
                                  backgroundColor: 'white',
                                  color: '#ff4d4f',
                                  border: '1px solid #ff4d4f',
                                  borderRadius: '4px',
                                  padding: '4px 12px',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fff1f0';
                                }}
                                onMouseOut={(e) => {
                                  e.currentTarget.style.backgroundColor = 'white';
                                }}
                              >
                                Delete
                              </Button>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Col>

            <Col lg={4}>
              <Card style={{ 
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
                marginBottom: '1.5rem'
              }}>
                <Card.Body style={{ padding: '24px' }}>
                  <h5 style={{ 
                    color: '#262626',
                    fontWeight: 600,
                    fontSize: '16px',
                    marginBottom: '20px'
                  }}>
                    Publishing
                  </h5>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ 
                      fontWeight: 500, 
                      color: '#262626',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Status
                    </Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </Form.Select>
                    <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      {formData.status === 'published'
                        ? 'Blog will be visible to the public'
                        : 'Save as draft for later'}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={{ 
                      fontWeight: 500, 
                      color: '#262626',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Category
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      placeholder="e.g., Technology, News"
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                  </Form.Group>

                  <hr style={{ margin: '20px 0', borderColor: '#f0f0f0' }} />

                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      style={{
                        backgroundColor: '#00579a',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(0,87,154,0.2)',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseOver={(e) => !loading && (e.currentTarget.style.backgroundColor = '#004a85')}
                      onMouseOut={(e) => !loading && (e.currentTarget.style.backgroundColor = '#00579a')}
                    >
                      {loading ? 'Saving...' : isEdit ? 'Update Blog' : 'Create Blog'}
                    </Button>
                    <Button
                      onClick={() => navigate('/blogs')}
                      disabled={loading}
                      style={{
                        backgroundColor: 'white',
                        color: '#595959',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        padding: '10px 20px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Card.Body>
              </Card>

              <Card style={{ 
                border: 'none',
                borderRadius: '8px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)'
              }}>
                <Card.Body style={{ padding: '24px' }}>
                  <h5 style={{ 
                    color: '#262626',
                    fontWeight: 600,
                    fontSize: '16px',
                    marginBottom: '20px'
                  }}>
                    Upload Files
                  </h5>
                  <Form.Group>
                    <Form.Label style={{ 
                      fontWeight: 500, 
                      color: '#262626',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      Add Images or Documents
                    </Form.Label>
                    <Form.Control
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx"
                      onChange={handleFileChange}
                      style={{
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        padding: '8px 12px',
                        fontSize: '14px'
                      }}
                    />
                    <Form.Text style={{ color: '#8c8c8c', fontSize: '12px' }}>
                      Upload up to 10 files (images or documents)
                    </Form.Text>
                  </Form.Group>

                  {files && files.length > 0 && (
                    <div className="mt-3">
                      <strong style={{ fontSize: '14px', color: '#262626' }}>
                        Selected files:
                      </strong>
                      <ul style={{ fontSize: '13px', marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                        {Array.from(files).map((file, index) => (
                          <li key={index} style={{ color: '#595959', marginBottom: '4px' }}>
                            {file.name} <span style={{ color: '#8c8c8c' }}>({formatFileSize(file.size)})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>

        <style>{`
          .quill-wrapper table {
            border-collapse: collapse;
            width: 100%;
            margin: 1em 0;
          }
          .quill-wrapper table td,
          .quill-wrapper table th {
            border: 1px solid #ddd;
            padding: 8px;
            min-width: 50px;
          }
          .quill-wrapper table tr:nth-child(even) {
            background-color: #f9f9f9;
          }
          .quill-wrapper table tr:hover {
            background-color: #f5f5f5;
          }
          .ql-toolbar.ql-snow {
            border: 1px solid #d9d9d9;
            border-radius: 6px 6px 0 0;
          }
          .ql-container.ql-snow {
            border: 1px solid #d9d9d9;
            border-top: none;
            border-radius: 0 0 6px 6px;
            font-size: 14px;
          }
        `}</style>
      </Container>
    </div>
  );
};

export default BlogForm;